import type {Values} from '@wix/devzai-utils-common';
import {DoppeProductsGridLayoutSize} from '../doppe-products-grid-layout-size';
import {DoppeActionLayoutMediaRatio} from '../doppe-action-layout';

/**
 * Predefined template IDs for service list pages.
 * Each template defines a different visual layout and behavior.
 */
export const DoppeServiceListPageTemplateId = {
    /**
     * Frameless template - clean grid layout without card borders.
     * Image and text displayed without container borders.
     * Uses DoppeProductsGridLayoutSize to control item size.
     */
    FramelessTemplate: 'FramelessTemplate',

    /**
     * Card template with bordered card containers.
     * Each service is displayed in a card with subtle borders.
     * Uses DoppeProductsGridLayoutSize to control item size.
     */
    CardTemplate: 'CardTemplate',

    /**
     * On-image template with text overlay on the service image.
     * Service info is displayed on top of the image.
     * Uses DoppeProductsGridLayoutSize to control item size.
     * Fixed image ratio (not editable).
     */
    OnImageTemplate: 'OnImageTemplate',

    /**
     * Row template with horizontal list layout.
     * Each service is displayed as a full-width row.
     * Uses flex column layout (not grid).
     * Does not support layout size or custom image ratio.
     */
    RowTemplate: 'RowTemplate',

    // Legacy template IDs - kept for backward compatibility
    /** @deprecated Use FramelessTemplate instead */
    GridTemplate: 'GridTemplate',
    /** @deprecated Use RowTemplate instead */
    ListTemplate: 'ListTemplate',
} as const;

export type DoppeServiceListPageTemplateId = Values<typeof DoppeServiceListPageTemplateId>;

/**
 * Template kind for service list pages.
 */
export const DoppeServiceListPageTemplateKind = {
    /**
     * A predefined template with a specific templateId.
     */
    Predefined: 'predefined',
} as const;

export type DoppeServiceListPageTemplateKind = Values<typeof DoppeServiceListPageTemplateKind>;

export const DoppeServicesImageRatio = DoppeActionLayoutMediaRatio;

export type DoppeServicesImageRatio = Values<typeof DoppeServicesImageRatio>;

/**
 * Template selector for service list pages.
 * Specifies which template to use for rendering the page.
 */
export type DoppeServiceListPageTemplate = DoppeServiceListPageTemplate.Predefined;

export namespace DoppeServiceListPageTemplate {
    /**
     * A predefined template with a specific templateId.
     */
    export interface Predefined {
        kind: typeof DoppeServiceListPageTemplateKind.Predefined;
        templateId: DoppeServiceListPageTemplateId;
    }
}

/**
 * Template-specific props for each service list page template.
 * Each template has its own set of configurable properties.
 */
export type DoppeServiceListPageTemplateProps = 
    | DoppeServiceListPageTemplateProps.FramelessTemplate
    | DoppeServiceListPageTemplateProps.CardTemplate
    | DoppeServiceListPageTemplateProps.OnImageTemplate
    | DoppeServiceListPageTemplateProps.RowTemplate
    // Legacy types for backward compatibility
    | DoppeServiceListPageTemplateProps.GridTemplate
    | DoppeServiceListPageTemplateProps.ListTemplate;

export namespace DoppeServiceListPageTemplateProps {
    /**
     * Props for the FramelessTemplate.
     * Clean grid layout without card borders.
     * Uses responsive grid with min/max widths based on layout size.
     */
    export interface FramelessTemplate {
        /**
         * Template identifier for discriminated union.
         */
        templateType: typeof DoppeServiceListPageTemplateId.FramelessTemplate;

        /**
         * Layout size for the services grid.
         * - Small: Smaller cards with different min widths for mobile (150px) and desktop (280px)
         * - Medium: Medium cards (min 380px, fluid max) - default
         */
        layoutSize: DoppeProductsGridLayoutSize;

        /**
         * Image aspect ratio for service images.
         */
        imageRatio: DoppeActionLayoutMediaRatio;
    }

    /**
     * Props for the CardTemplate.
     * Grid layout with bordered card containers.
     * Uses responsive grid with min/max widths based on layout size.
     */
    export interface CardTemplate {
        /**
         * Template identifier for discriminated union.
         */
        templateType: typeof DoppeServiceListPageTemplateId.CardTemplate;

        /**
         * Layout size for the services grid.
         * - Small: Smaller cards with different min widths for mobile (150px) and desktop (280px)
         * - Medium: Medium cards (min 380px, fluid max) - default
         */
        layoutSize: DoppeProductsGridLayoutSize;

        /**
         * Image aspect ratio for service images.
         */
        imageRatio: DoppeActionLayoutMediaRatio;
    }

    /**
     * Props for the OnImageTemplate.
     * Grid layout with text overlay on images.
     * Uses responsive grid with min/max widths based on layout size.
     * Image ratio is fixed and not editable.
     */
    export interface OnImageTemplate {
        /**
         * Template identifier for discriminated union.
         */
        templateType: typeof DoppeServiceListPageTemplateId.OnImageTemplate;

        /**
         * Layout size for the services grid.
         * - Small: Smaller cards with different min widths for mobile (150px) and desktop (280px)
         * - Medium: Medium cards (min 380px, fluid max) - default
         */
        layoutSize: DoppeProductsGridLayoutSize;
    }

    /**
     * Props for the RowTemplate.
     * Horizontal list layout with flex column.
     * Does not support layout size or custom image ratio.
     */
    export interface RowTemplate {
        /**
         * Template identifier for discriminated union.
         */
        templateType: typeof DoppeServiceListPageTemplateId.RowTemplate;
    }

    // Legacy types for backward compatibility
    /** @deprecated Use FramelessTemplate instead */
    export interface GridTemplate {
        layoutSize: DoppeProductsGridLayoutSize;
        imageRatio: DoppeActionLayoutMediaRatio;
    }

    /** @deprecated Use RowTemplate instead */
    export interface ListTemplate {
        layoutSize: DoppeProductsGridLayoutSize;
        imageRatio: DoppeActionLayoutMediaRatio;
    }
}

/**
 * Common props shared by all service list page templates.
 * These props are template-agnostic and control shared behavior.
 */
export interface DoppeServiceListPageCommonProps {
    /**
     * Whether to show the service price.
     * @default true
     */
    showPrice: boolean;

    /**
     * Whether to show the service description.
     * @default true
     */
    showDescription: boolean;

    /**
     * Whether to show the service duration.
     * @default true
     */
    showDuration: boolean;

    /**
     * Whether to show the service location.
     * @default true
     */
    showLocation: boolean;

    /**
     * Whether to show the service type badge (e.g., "Available Online").
     * @default true
     */
    showTypeBadge: boolean;

    /**
     * Whether to show the Book Now button on service cards.
     * @default false
     */
    showBookNowButton: boolean;

    /**
     * Custom text for the Book Now button.
     * @default "Book now"
     */
    bookButtonText: string;

    /**
     * Whether to show the Read More button on service cards.
     * @default false
     */
    showReadMoreButton: boolean;

    /**
     * Custom text for the Read More button.
     * @default "Read More"
     */
    readMoreButtonText: string;
}

/**
 * Creates default template settings for a service list page.
 * Uses the FramelessTemplate by default.
 */
export function doppeServiceListPageTemplateCreateDefault(): DoppeServiceListPageTemplate {
    return {
        kind: DoppeServiceListPageTemplateKind.Predefined,
        templateId: DoppeServiceListPageTemplateId.FramelessTemplate,
    };
}

/**
 * Creates default template props for a given template.
 */
export function doppeServiceListPageTemplatePropsCreateDefault(
    templateId: DoppeServiceListPageTemplateId
): DoppeServiceListPageTemplateProps {
    switch (templateId) {
        case DoppeServiceListPageTemplateId.FramelessTemplate:
            return {
                templateType: DoppeServiceListPageTemplateId.FramelessTemplate,
                layoutSize: DoppeProductsGridLayoutSize.Medium,
                imageRatio: DoppeActionLayoutMediaRatio.Square,
            };
        case DoppeServiceListPageTemplateId.CardTemplate:
            return {
                templateType: DoppeServiceListPageTemplateId.CardTemplate,
                layoutSize: DoppeProductsGridLayoutSize.Medium,
                imageRatio: DoppeActionLayoutMediaRatio.Square,
            };
        case DoppeServiceListPageTemplateId.OnImageTemplate:
            return {
                templateType: DoppeServiceListPageTemplateId.OnImageTemplate,
                layoutSize: DoppeProductsGridLayoutSize.Medium,
            };
        case DoppeServiceListPageTemplateId.RowTemplate:
            return {
                templateType: DoppeServiceListPageTemplateId.RowTemplate,
            };
        // Legacy templates - return frameless defaults
        case DoppeServiceListPageTemplateId.GridTemplate:
        case DoppeServiceListPageTemplateId.ListTemplate:
        default:
            return {
                templateType: DoppeServiceListPageTemplateId.FramelessTemplate,
                layoutSize: DoppeProductsGridLayoutSize.Medium,
                imageRatio: DoppeActionLayoutMediaRatio.Square,
            };
    }
}

/**
 * Creates default common props for a service list page.
 */
export function doppeServiceListPageCommonPropsCreateDefault(): DoppeServiceListPageCommonProps {
    return {
        showPrice: true,
        showDescription: true,
        showDuration: true,
        showLocation: true,
        showTypeBadge: true,
        showBookNowButton: false,
        bookButtonText: 'Book now',
        showReadMoreButton: false,
        readMoreButtonText: 'Read More',
    };
}

/**
 * Type guard to check if template props are for FramelessTemplate.
 */
export function doppeServiceListPageTemplatePropsIsFramelessTemplate(
    props: DoppeServiceListPageTemplateProps
): props is DoppeServiceListPageTemplateProps.FramelessTemplate {
    return 'templateType' in props && props.templateType === DoppeServiceListPageTemplateId.FramelessTemplate;
}

/**
 * Type guard to check if template props are for CardTemplate.
 */
export function doppeServiceListPageTemplatePropsIsCardTemplate(
    props: DoppeServiceListPageTemplateProps
): props is DoppeServiceListPageTemplateProps.CardTemplate {
    return 'templateType' in props && props.templateType === DoppeServiceListPageTemplateId.CardTemplate;
}

/**
 * Type guard to check if template props are for OnImageTemplate.
 */
export function doppeServiceListPageTemplatePropsIsOnImageTemplate(
    props: DoppeServiceListPageTemplateProps
): props is DoppeServiceListPageTemplateProps.OnImageTemplate {
    return 'templateType' in props && props.templateType === DoppeServiceListPageTemplateId.OnImageTemplate;
}

/**
 * Type guard to check if template props are for RowTemplate.
 */
export function doppeServiceListPageTemplatePropsIsRowTemplate(
    props: DoppeServiceListPageTemplateProps
): props is DoppeServiceListPageTemplateProps.RowTemplate {
    return 'templateType' in props && props.templateType === DoppeServiceListPageTemplateId.RowTemplate;
}

/**
 * Check if template props support layout size setting.
 * Only Frameless, Card, and OnImage templates support layout size.
 */
export function doppeServiceListPageTemplatePropsSupportsLayoutSize(
    props: DoppeServiceListPageTemplateProps
): props is DoppeServiceListPageTemplateProps.FramelessTemplate 
    | DoppeServiceListPageTemplateProps.CardTemplate 
    | DoppeServiceListPageTemplateProps.OnImageTemplate {
    return doppeServiceListPageTemplatePropsIsFramelessTemplate(props) ||
           doppeServiceListPageTemplatePropsIsCardTemplate(props) ||
           doppeServiceListPageTemplatePropsIsOnImageTemplate(props);
}/**
 * Check if template props support image ratio setting.
 * Only Frameless and Card templates support custom image ratio.
 * OnImage and Row templates have fixed image ratios.
 */
export function doppeServiceListPageTemplatePropsSupportsImageRatio(
    props: DoppeServiceListPageTemplateProps
): props is DoppeServiceListPageTemplateProps.FramelessTemplate 
    | DoppeServiceListPageTemplateProps.CardTemplate {
    return doppeServiceListPageTemplatePropsIsFramelessTemplate(props) ||
           doppeServiceListPageTemplatePropsIsCardTemplate(props);
}
